import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const CitationAnalyzerViewResult = ({result}) => {
  const getScoreColor = (score) => {
    if (score >= 0.8) return 'bg-success/20 text-success';
    if (score >= 0.6) return 'bg-warning/20 text-warning';
    return 'bg-error/20 text-error';
  };

  const getLevelColor = (level) => {
    switch(level) {
      case 'high':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Citations Section */}
        <div className="space-y-4">
          {/* Citations Overview Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.Books size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Citations Overview</h3>
              </div>

              <div className="space-y-3">
                {/* Citation Count */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Citations Found</span>
                    <div className="flex gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(result?.citation_analysis?.citations_found?.confidence)}`}>
                        {result?.citation_analysis?.citations_found?.count} citations
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(result?.citation_analysis?.citations_found?.confidence)}`}>
                        {Math.round(result?.citation_analysis?.citations_found?.confidence * 100)}% confidence
                      </span>
                    </div>
                  </div>
                </div>

                {/* Styles Detected */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Citation Styles Detected</h4>
                  <div className="flex flex-wrap gap-2">
                    {result?.citation_analysis?.citations_found?.styles_detected?.map((style, index) => (
                      <span key={index} className="px-2 py-1 bg-secondary/10 text-secondary rounded-full text-sm">
                        {style}
                      </span>
                    )) || (
                      <span className="text-sm text-base-content/70">No citation styles detected</span>
                    )}
                  </div>
                </div>

                {/* Individual Citations */}
                <div className="space-y-3">
                  {result?.citation_analysis?.citations?.map((citation, index) => (
                    <div key={index} className="p-3 bg-base-200 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Citation {index + 1}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(citation.confidence)}`}>
                          {Math.round(citation.confidence * 100)}% confidence
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="text-sm bg-base-300 p-2 rounded">
                          {citation.text}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 bg-info/10 text-info rounded-full text-xs">
                            Style: {citation.style}
                          </span>
                          <span className="px-2 py-1 bg-info/10 text-info rounded-full text-xs">
                            Type: {citation.type}
                          </span>
                        </div>
                        {citation.components && (
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {citation.components.authors && (
                              <div className="col-span-2">
                                <span className="font-medium">Authors: </span>
                                {citation.components.authors.join(', ')}
                              </div>
                            )}
                            {citation.components.year && (
                              <div>
                                <span className="font-medium">Year: </span>
                                {citation.components.year}
                              </div>
                            )}
                            {citation.components.title && (
                              <div className="col-span-2">
                                <span className="font-medium">Title: </span>
                                {citation.components.title}
                              </div>
                            )}
                            {citation.components.source && (
                              <div className="col-span-2">
                                <span className="font-medium">Source: </span>
                                {citation.components.source}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!result?.citation_analysis?.citations || result?.citation_analysis?.citations.length === 0) && (
                    <div className="p-3 bg-base-200 rounded-lg text-base-content/70">
                      No citations found in the text
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Validation & Analysis Section */}
        <div className="space-y-4">
          {/* Validation Results Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.CheckSquare size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Validation Results</h3>
              </div>

              <div className="space-y-3">
                {/* Completeness Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Completeness</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(result?.citation_analysis?.validation_results?.completeness)}`}>
                      {Math.round(result?.citation_analysis?.validation_results?.completeness * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getScoreColor(result?.citation_analysis?.validation_results?.completeness)}`}
                      style={{width: `${result?.citation_analysis?.validation_results?.completeness * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Formatting Accuracy */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Formatting Accuracy</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(result?.citation_analysis?.validation_results?.formatting_accuracy)}`}>
                      {Math.round(result?.citation_analysis?.validation_results?.formatting_accuracy * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getScoreColor(result?.citation_analysis?.validation_results?.formatting_accuracy)}`}
                      style={{width: `${result?.citation_analysis?.validation_results?.formatting_accuracy * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Missing Elements */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Missing Elements</h4>
                  <div className="space-y-1">
                    {result?.citation_analysis?.validation_results?.missing_elements?.map((element, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <PhosphorIcons.WarningCircle className="text-warning" size={16}/>
                        <span>{element}</span>
                      </div>
                    )) || (
                      <span className="text-sm text-success">No missing elements found</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analysis Confidence Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.Gauge size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Analysis Confidence</h3>
              </div>

              <div className="space-y-3">
                {/* Overall Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Overall Score</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(result?.metadata?.analysis_confidence?.overall_score)}`}>
                      {Math.round(result?.metadata?.analysis_confidence?.overall_score * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getScoreColor(result?.metadata?.analysis_confidence?.overall_score)}`}
                      style={{width: `${result?.metadata?.analysis_confidence?.overall_score * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Style Recognition */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Style Recognition</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getScoreColor(result?.metadata?.analysis_confidence?.style_recognition)}`}>
                      {Math.round(result?.metadata?.analysis_confidence?.style_recognition * 100)}%
                    </span>
                  </div>
                </div>

                {/* Validation Strength */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Validation Strength</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getLevelColor(result?.metadata?.analysis_confidence?.validation_strength)}`}>
                      {result?.metadata?.analysis_confidence?.validation_strength}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CitationAnalyzerViewResult;